import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Database, get, ref } from "@angular/fire/database";
import { catchError, from, map, Observable, of, switchMap } from "rxjs";
import { environment } from "../../../environments/environment";
import { IComentario } from "../../interfaces/busqueda";
import { LoadingService } from "../../loading/loading.service";
@Injectable({
  providedIn: "root",
})
export class BusquedaService {
  public db: Database = inject(Database);
  public loadingService = inject(LoadingService);
  constructor(private http: HttpClient) {}

  public campoEncontrado = "";
  public buscar = "";

  busqueda(payload: any): Observable<any> {
    return this.http.post(`${environment.apiSearch.busqueda}`, payload).pipe(
      switchMap((data: any) => {
        if (payload.idsTemas && payload.buscar && data.ids) {
          return this.buscarAPI(
            data.ids,
            "busquedaSencilla",
            payload.buscar
          ).then((res) => {
            return res;
          });
        } else {
          return of(data);
        }
      }),
      catchError((error: any) => {
        return of({
          error: true,
          message: "Error en busqueda",
          entradas: [],
        });
      })
    );
  }

  busquedaAvanzada(payload: any): Observable<any> {
    return this.http
      .post(`${environment.apiSearch.busquedaAvanzada}`, payload)
      .pipe(
        switchMap((data: any) => {
          if (data.ids && data.ids.length > 0) {
            return this.buscarAPI(
              data.ids,
              "busquedaAvanzada",
              payload.paramsBusqueda.texto
            ).then((res) => {
              return res;
            });
          } else {
            return of(data);
          }
        }),
        catchError((error: any) => {
          return of({
            error: true,
            message: "Error en busqueda",
            entradas: [],
          });
        })
      );
  }

  async buscarAPI(
    ids: any[],
    tipoBusqueda: string,
    buscar: string
  ): Promise<any> {
    this.buscar = buscar;
    this.loadingService.show();

    try {
      const entradasPromises = ids.map(async (id: any) => {
        const entradaRefPath = `/entrada/${id.idTema}/${id.idEntrada}`;
        const historicoRefPath = `/historico/${id.idTema}/${id.idEntrada}`;
        const comentariosRefPath = `/comentarios/${id.idEntrada}`;

        const [entradaRes, historicoRes, comentariosRes] = await Promise.all([
          this.fetchWithCache(entradaRefPath),
          this.fetchWithCache(historicoRefPath),
          this.fetchWithCache(comentariosRefPath),
        ]);

        const hasData = [entradaRes, historicoRes, comentariosRes].some(
          (res) => res && Object.keys(res).length > 1
        );

        if (!hasData) return null;
        return {
          entradaRes: {
            ...entradaRes,
            idTema: id.idTema,
            idEntrada: id.idEntrada,
          },
          historicoRes,
          comentariosRes,
        };
      });

      const entradasData = await (
        await Promise.all(entradasPromises)
      ).filter((ent) => ent !== null);

      let processedEntradas = entradasData.map((entradaData: any) => {
        const { entradaRes, historicoRes, comentariosRes } = entradaData;
        const comentariosConIds = comentariosRes
          ? Object.entries(comentariosRes).map(([id, data]) => ({
              id,
              ...(data as IComentario),
            }))
          : [];

        const entrada = {
          id: `${entradaRes.idTema}/${entradaRes.idEntrada}`,
          idTema: entradaRes.idTema,
          idEntrada: entradaRes.idEntrada,
          ...entradaRes,
          ...historicoRes,
          historico: historicoRes,
          isHistorico:
            historicoRes && Object.keys(historicoRes).length > 0 ? true : false,
          comentarios: comentariosConIds,
          tamBusqueda: buscar ? buscar.length : 0,
          encontrado: [],
          campoEncontrado: "",
        };

        entrada.tamBusqueda = buscar ? buscar.length : 0;
        entrada.encontrado = [];
        const encontrado: any[] = [];
        this.campoEncontrado = "";

        if (
          entrada.hasOwnProperty("asunto") &&
          this.esCampoValido(entrada.asunto)
        ) {
          this.setCampoEncontrado("asunto");
          encontrado.push(this.getMatches(entrada.asunto));
        }

        if (
          entrada.hasOwnProperty("descripcion") &&
          this.esCampoValido(entrada.descripcion)
        ) {
          this.setCampoEncontrado("descripcion");
          encontrado.push(this.getMatches(entrada.descripcion));
        }

        if (
          entrada.hasOwnProperty("remitente_descripcion") &&
          this.esCampoValido(entrada.remitente_descripcion)
        ) {
          this.setCampoEncontrado("remitente");
          encontrado.push(this.getMatches(entrada.remitente_descripcion));
        }

        if (
          entrada.hasOwnProperty("tema_descripcion") &&
          this.esCampoValido(entrada.tema_descripcion)
        ) {
          this.setCampoEncontrado("tema");
          encontrado.push(this.getMatches(entrada.tema_descripcion));
        }

        if (
          entrada.hasOwnProperty("tipo_descripcion") &&
          this.esCampoValido(entrada.tipo_descripcion)
        ) {
          this.setCampoEncontrado("tipo");
          encontrado.push(this.getMatches(entrada.tipo_descripcion));
        }

        if (
          entrada.hasOwnProperty("modo_entrada_descripcion") &&
          this.esCampoValido(entrada.modo_entrada_descripcion)
        ) {
          this.setCampoEncontrado("modoEntrada");
          encontrado.push(this.getMatches(entrada.modo_entrada_descripcion));
        }

        if (
          entrada.hasOwnProperty("prioridad_descripcion") &&
          this.esCampoValido(entrada.prioridad_descripcion)
        ) {
          this.setCampoEncontrado("prioridad");
          encontrado.push(this.getMatches(entrada.prioridad_descripcion));
        }

        if (
          entrada.hasOwnProperty("empresa_descripcion") &&
          this.esCampoValido(entrada.empresa_descripcion)
        ) {
          this.setCampoEncontrado("empresa");
          encontrado.push(this.getMatches(entrada.empresa_descripcion));
        }

        if (
          entrada.hasOwnProperty("folio") &&
          this.esCampoValido(entrada.folio)
        ) {
          this.setCampoEncontrado("folio");
          encontrado.push(this.getMatches(entrada.folio));
        }

        if (entrada.hasOwnProperty("delegado_descripcion")) {
          if (
            typeof entrada.delegado_descripcion === "object" &&
            Array.isArray(entrada.delegado_descripcion)
          ) {
            for (let k = 0; k < entrada.delegado_descripcion.length; k++) {
              if (this.esCampoValido(entrada.delegado_descripcion[k])) {
                const descripcion = entrada.delegado_descripcion[k];
                this.setCampoEncontrado("delegadoA");
                encontrado.push(this.getMatches(descripcion));
              }
            }
          } else {
            if (
              typeof entrada.delegado_descripcion === "string" &&
              this.esCampoValido(entrada.delegado_descripcion)
            ) {
              this.setCampoEncontrado("delegadoA");
              encontrado.push(this.getMatches(entrada.delegado_descripcion));
            }
          }
        }

        if (
          entrada.hasOwnProperty("estatus_descripcion") &&
          this.esCampoValido(entrada.estatus_descripcion)
        ) {
          this.setCampoEncontrado("estatus");
          encontrado.push(this.getMatches(entrada.estatus_descripcion));
        }

        if (
          entrada.hasOwnProperty("bb_enviado_descripcion") &&
          this.esCampoValido(entrada.bb_enviado_descripcion)
        ) {
          this.setCampoEncontrado("enviadoPor");
          encontrado.push(this.getMatches(entrada.bb_enviado_descripcion));
        }

        if (
          entrada.hasOwnProperty("fechaInicio") &&
          this.esCampoValido(entrada.fechaInicio)
        ) {
          const fechaInicio = entrada.fechaInicio.toLowerCase().includes("t")
            ? entrada.fechaInicio.split("T")[0]
            : entrada.fechaInicio;
          this.setCampoEncontrado("fechaInicio");
          encontrado.push(this.getMatches(fechaInicio));
        }

        if (
          entrada.hasOwnProperty("fechaEnt") &&
          this.esCampoValido(entrada.fechaEnt)
        ) {
          const fechaEnt = entrada.fechaEnt.toLowerCase().includes("t")
            ? entrada.fechaEnt.split("T")[0]
            : entrada.fechaEnt;
          this.setCampoEncontrado("fechaEnt");
          encontrado.push(this.getMatches(fechaEnt));
        }

        if (
          entrada.hasOwnProperty("fechaFin") &&
          this.esCampoValido(entrada.fechaEnt)
        ) {
          const fechaFin = entrada.fechaFin.toLowerCase().includes("t")
            ? entrada.fechaFin.split("T")[0]
            : entrada.fechaFin;
          this.setCampoEncontrado("fechaFin");
          encontrado.push(this.getMatches(fechaFin));
        }

        if (
          entrada.hasOwnProperty("comentarios") &&
          entrada.comentarios.length > 0
        ) {
          for (const comentario of entrada.comentarios) {
            if (
              comentario.hasOwnProperty("descripcion") &&
              this.esCampoValido(comentario.descripcion)
            ) {
              this.setCampoEncontrado(comentario.id);
              encontrado.push(this.getMatches(comentario.descripcion));
            }
            if (
              comentario.hasOwnProperty("bb_enviado_descripcion") &&
              this.esCampoValido(comentario.bb_enviado_descripcion)
            ) {
              this.setCampoEncontrado(comentario.id);
              encontrado.push(
                this.getMatches(comentario.bb_enviado_descripcion)
              );
            }
            if (
              comentario.hasOwnProperty("fecha") &&
              this.esCampoValido(comentario.fecha)
            ) {
              this.setCampoEncontrado(comentario.id);
              encontrado.push(this.getMatches(comentario.fecha));
            }
            if (
              comentario.hasOwnProperty("usuario") &&
              this.esCampoValido(comentario.usuario)
            ) {
              this.setCampoEncontrado(comentario.id);
              encontrado.push(this.getMatches(comentario.usuario));
            }
          }
        }

        if (entrada.hasOwnProperty("archivos") && entrada.archivos.length > 0) {
          for (const archivo of entrada.archivos) {
            if (
              archivo.hasOwnProperty("name") &&
              this.esCampoValido(archivo.name)
            ) {
              this.setCampoEncontrado("archivos");
              encontrado.push(this.getMatches(archivo.name));
            }
          }
        }
        entrada.encontrado = [...encontrado];
        entrada.fechaInicio = entrada.fechaInicio
          ? entrada.fechaInicio.split("T")[0]
          : entrada.fechaEnt
          ? entrada.fechaEnt.split("T")[0]
          : "";
        entrada.campoEncontrado = this.campoEncontrado;

        return entrada;
      });

      this.loadingService.hide();

      processedEntradas.sort((a, b) => {
        let fecha;
        let fechaComparar;

        if (a.tsLastUpdate === undefined && a.tsCreation === undefined) {
          fecha = new Date(a.fechaInicio);
        } else {
          fecha =
            a.tsLastUpdate === undefined ||
            new Date(a.tsCreation) > new Date(a.tsLastUpdate)
              ? new Date(a.tsCreation)
              : new Date(a.tsLastUpdate);
        }

        if (b.tsLastUpdate === undefined && b.tsCreation === undefined) {
          fechaComparar = new Date(b.fechaInicio);
        } else {
          fechaComparar =
            b.tsLastUpdate === undefined ||
            new Date(b.tsCreation) > new Date(b.tsLastUpdate)
              ? new Date(b.tsCreation)
              : new Date(b.tsLastUpdate);
        }

        return fecha < fechaComparar ? 1 : fecha > fechaComparar ? -1 : 0;
      });
      return { entradas: processedEntradas, ids };
    } catch (error) {
      this.loadingService.hide();
      console.error("Error al procesar las entradas:", error);
      return { entradas: [], ids };
    }
  }

  esCampoValido(campo: string): boolean {
    if (!campo || !this.buscar) {
      return false;
    }
    const caracteresEscapados = this.buscar
      .replace(/[-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&")
      .toLowerCase();
    return campo.toLowerCase().search(caracteresEscapados) > -1;
  }

  setCampoEncontrado(nombreCampo: string): void {
    if (this.campoEncontrado.length === 0) {
      this.campoEncontrado = nombreCampo;
    } else {
      this.campoEncontrado += `|${nombreCampo}`;
    }
  }
  getMatches(campoEntrada: any): object {
    if (!campoEntrada || !this.buscar) {
      return {
        texto: campoEntrada + " | ",
        indices: 0,
      };
    }
    const caracteresEscapados = this.buscar
      .replace(/[-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&")
      .toLowerCase();
    const wregex = new RegExp(caracteresEscapados, "g");

    let matches = [];
    let match;
    while ((match = wregex.exec(campoEntrada.toLowerCase())) !== null) {
      matches.push(match.index);
    }
    return {
      texto: campoEntrada + " | ",
      indices: matches,
    };
  }

  obtenerHistorico(idTema: string, idEntrada: string): Observable<any> {
    const refCatalogo = ref(this.db, `historico/${idTema}/${idEntrada}`);
    return from(get(refCatalogo)).pipe(
      map((snapshot) => {
        if (snapshot.exists()) {
          return snapshot.val();
        } else {
          return null;
        }
      })
    );
  }

  private cache: Map<string, any> = new Map();
  private ongoingRequests: Map<string, Promise<any>> = new Map();

  private fetchWithCache(refPath: string): Promise<any> {
    if (this.cache.has(refPath)) {
      return Promise.resolve(this.cache.get(refPath));
    }
    if (this.ongoingRequests.has(refPath)) {
      return this.ongoingRequests.get(refPath)!;
    }
    const request = get(ref(this.db, refPath))
      .then((snapshot) => {
        const data = snapshot.val();
        if (data === null) {
          if (!this.cache.has(refPath)) {
            this.cache.set(refPath, {});
          }
          return {};
        }
        const cachedData = this.cache.get(refPath);
        if (cachedData && JSON.stringify(cachedData) === JSON.stringify(data)) {
          return cachedData;
        }
        this.cache.set(refPath, { ...data });
        return { ...data };
      })
      .finally(() => {
        this.ongoingRequests.delete(refPath);
      });
    this.ongoingRequests.set(refPath, request);
    return request;
  }
}
